import { Button as ButtonBaseM, CircularProgress, IconButton, IconButtonProps, makeStyles, styled } from '@material-ui/core';
import { useLinked } from '@type-r/react';
import * as React from 'react';
import { PropsWithChildren } from 'react';
import { ChildrenWithQaId, useQaId } from './qa-ids';
import { colors } from '../common';

const ButtonBase = ({ 'qa-id' : qaId, children, ...props } : ChildrenWithQaId<React.ComponentProps<typeof ButtonBaseM>> ) => {
    const id = useQaId( qaId || children );

    return <ButtonBaseM {...props} data-qa-id={ id }> { children } </ButtonBaseM>
}

export interface XlargeButtonProps extends IconButtonProps {
    children?: JSX.Element | JSX.Element[],
    icon?: string,
    ticked?: boolean
}

export const XLargeButton = ({children, icon, 'qa-id' : qaId, ticked, ...rest} : ChildrenWithQaId<XlargeButtonProps>) => {
    const classes = useXLargeButtonStyle({ticked});
    const id = useQaId( qaId || children );
    return (
        <IconButton className={ classes.root + ' ' + classes.label } {...rest} data-qa-id={ id }>
            <img className={ classes.image } src={ icon } alt={ id } />
            <div className={ classes.content }>{ children }</div>
            { ticked && <img className={ classes.tick } src="/assets/images/tick.png" alt="Tick" /> }
        </IconButton>)
}

const useXLargeButtonStyle = makeStyles({
    root: {
        width: '100%',
        height: '110px',
        background: (props: XlargeButtonProps) => props.ticked ? '#E9F9F3' : '#FFFFFF',
        border: (props: XlargeButtonProps) => props.ticked ? '1px solid #58C091' : '1px solid #E7F2F9',
        borderRadius: '4px',
        boxSizing: 'border-box'

    },
    image: {
        width: '83.81px',
        height: '80px',
        background: (props: XlargeButtonProps) => props.ticked ? '#58C091' : '#F4FAFD',
        borderRadius: '4px'
    },
    label: {
        justifyContent: 'normal'
    },
    tick: {
        marginLeft: 'auto',
        paddingRight: '20px'
    },
    content: {
        width: '70%',
        minWidth: '200px',
        wordBreak: 'break-word',
        fontFamily: 'Montserrat',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '20px',
        lineHeight: '24px',
        display: 'block',
        alignItems: 'center',
        textAlign: 'left',
        color: (props: XlargeButtonProps) => props.ticked ? '#58C091' : '#3B84CD',
        flex: 'none',
        order: 0,
        flexGrow: 0,
        paddingBottom: '10px',
        paddingLeft: '10px',
        '& span': {

            fontWeight: '600',
            fontSize: '13px',
            lineHeight: '16px',
            color: '#1F2530'
        },

    }
});

export const AttachButton = (props: IconButtonProps) => {
    const id = useQaId( 'attach-button' );
    return (
        <IconButton size="small" { ...props } data-qa-id={ id } aria-label="Attach">
            <img src="/assets/images/circle-attach.png" alt="Attach" />
        </IconButton>
    )
}

export const EditButton = (props: IconButtonProps) => {
    const id = useQaId( 'edit-button' );
    return (
        <IconButton aria-label="Edit" size="small" { ...props } data-qa-id={ id }>
            <img src="/assets/images/circle-edit.png" alt="Edit" />
        </IconButton>
    )
}

function IoOnClick<T extends PropsWithChildren<object>>(Component: React.ComponentType<T>) {
    const C = Component as any;

    return function IoOnClickWrapper({ children, onClick, ...props }: IOWrapperProps<T>) {
        const pendingIO = useLinked(false);

        async function ioWrapper(e) {
            try {
                pendingIO.true();
                await onClick(e);
            }
            finally {
                pendingIO.false();
            }
        }

        return (
            <C onClick={ ioWrapper } { ...props }>
                { pendingIO.value ? <CircularProgress size={ 20 } /> : children }
            </C>
        )
    }
}

type IOWrapperProps<T extends PropsWithChildren<object>> = Omit<T, 'onClick'> & {
    onClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<any>
}

const buttonStyles = {
    boxSizing: 'content-box',
    marginLeft: 8,
    marginRight: 8,
    borderRadius: 18,
    paddingLeft: 16,
    paddingRight: 16,
    fontFamily: 'Montserrat',
    fontSize: 14,
    boxShadow: 'none',
    textTransform: 'none',
}

const defaultPrimaryColor = props => colors[ props.color ] || '#4bb885';

export const PrimaryButton = styled(ButtonBase)({
    ...(buttonStyles as any),
    backgroundColor: defaultPrimaryColor,
    color: props => props.color === 'yellow' ? 'black' : 'white',
    borderWidth: 2,
    borderColor: defaultPrimaryColor,

    paddingTop: 1,
    paddingBottom: 1,

    '&:hover': {
        color: 'white',
        boxShadow: '2px 0 4px 0 rgba(0,0,0,0.1)',
        backgroundColor: defaultPrimaryColor,
    },

    '&:active': {
        backgroundColor: defaultPrimaryColor,
    },

    '&:focus': {
        border: '2px solid #0AC3F3',
        boxShadow: '0 0 2px 0 #0AC3F3, 2px 0 4px 0 rgba(0,0,0,0.1)',
        borderWidth: 0,
        outline: 'none',
    },

    '&:disabled': {
        backgroundColor: 'rgba(102,138,165,0.25)',
        opacity: 0.75,
        color: '#FFF',
        fontWeight: 500,
    }
}) as React.ComponentType<Omit<React.ComponentProps<typeof ButtonBase>, 'color'> & { color?: keyof typeof colors }>

export const IoIconButton = IoOnClick(IconButton);

/**
 * Currently there are two types of buttons. Regular `Button` and the colored one for the primary action.
 */
export const Button = styled(ButtonBase)({
    ...(buttonStyles as any),

    paddingTop: 0,
    paddingBottom: 0,

    backgroundColor: '#FFF',
    border: '1px solid #668AA5',
    color: '#668AA5',
    fontWeight: 500,

    '&:focus': {
        border: '1px solid #0098DB',
        boxShadow: '0 0 2px 0 #0AC3F3, 2px 0 4px 0 rgba(0,0,0,0.1)',
        outline: 'none',
        color: '#0098DB',
        fontWeight: 700,
    },

    '&:hover': {
        border: '1px solid #0098DB',
        color: '#0098DB',
        backgroundColor: 'white',
        fontWeight: 600,
        boxShadow: '2px 0 4px 0 rgba(0,0,0,0.1)'
    },

    '&:active': {
        color: '#668AA5',
        fontWeight: 500,
        border: '1px solid #668AA5',
        backgroundColor: 'white'//'#F4FAFD'
    }
})

export const IoButton = IoOnClick(Button);